import React, { useState, useMemo, useRef, useEffect } from "react"
import { Button, Tag, Flex, Card, Spin, Select } from "antd"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { updateCrewProfile, getCrewProfile } from "store/crew-profiles"
import { toaster } from "components/common/Toast"
import AvailabilityModal from "./modal/AvailabilityModal"
import BookingConflictModal from "../ScheduledTimeOff/components/BookingConflictModal"
import styles from "./index.module.css"
import PropTypes from "prop-types"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import NoAvailability from "ui/Icon/DSIcon/NoAvailability"
import { Rotate } from "ui/Icon/DSIcon/Rotate"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import moment from "moment"

const Availability = ({ crewMemberId, can_edit }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedAvailabilityIndex, setSelectedAvailabilityIndex] = useState(0)
  const scrollContainerRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const [bookingConflictOpen, setBookingConflictOpen] = useState(false)
  const [conflictBookings, setConflictBookings] = useState([])
  const [conflictCrewName, setConflictCrewName] = useState("")
  const [validationErrors, setValidationErrors] = useState({})
  const [selectedYear, setSelectedYear] = useState(null)

  // Get availability data from Redux store (populated from profile API)
  const { data: availabilitiesData = [] } = useSelector((state) => state.crewProfiles.availability)
  const { loading: updating } = useSelector((state) => state.crewProfiles.update)
  const { loading: profileLoading } = useSelector((state) => state.crewProfiles.profile)

  // Check if there are any seasonal availabilities
  const hasSeasonalAvailabilities = useMemo(() => {
    return availabilitiesData.some((availability) => availability.all_year === false)
  }, [availabilitiesData])

  // Extract unique years from seasonal availabilities
  const availableYears = useMemo(() => {
    if (!hasSeasonalAvailabilities) return []

    const years = new Set()

    availabilitiesData.forEach((availability) => {
      // Only process seasonal availabilities
      if (availability.all_year === false && availability.season_start && availability.season_end) {
        const startYear = moment(availability.season_start).year()
        const endYear = moment(availability.season_end).year()

        // Add both start and end years
        years.add(startYear)
        years.add(endYear)
      }
    })

    // Convert to array and sort in ascending order
    return Array.from(years).sort((a, b) => a - b)
  }, [availabilitiesData, hasSeasonalAvailabilities])

  // Set default year when availableYears changes
  useEffect(() => {
    if (availableYears.length === 0) {
      setSelectedYear(null)
      return
    }

    const currentYear = new Date().getFullYear()
    if (availableYears.includes(currentYear)) {
      setSelectedYear(currentYear)
    } else {
      setSelectedYear(availableYears[0])
    }
  }, [availableYears])

  // Filter availabilities by selected year
  const filteredAvailabilities = useMemo(() => {
    if (!hasSeasonalAvailabilities || !selectedYear) {
      return availabilitiesData
    }

    return availabilitiesData.filter((availability) => {
      // Keep all-year availabilities
      if (availability.all_year === true) {
        return true
      }

      // Filter seasonal availabilities by year
      if (availability.season_start && availability.season_end) {
        const startYear = moment(availability.season_start).year()
        const endYear = moment(availability.season_end).year()

        // Show availability if either start or end year matches selected year
        return startYear <= selectedYear && endYear >= selectedYear
      }

      return false
    })
  }, [availabilitiesData, hasSeasonalAvailabilities, selectedYear])

  // Reset selected availability index when filtered data changes
  useEffect(() => {
    if (filteredAvailabilities.length > 0 && selectedAvailabilityIndex >= filteredAvailabilities.length) {
      setSelectedAvailabilityIndex(0)
    }
  }, [filteredAvailabilities, selectedAvailabilityIndex])

  // Get the currently selected availability
  const selectedAvailability = useMemo(() => {
    if (!filteredAvailabilities || filteredAvailabilities.length === 0) return null
    return filteredAvailabilities[selectedAvailabilityIndex] || filteredAvailabilities[0]
  }, [filteredAvailabilities, selectedAvailabilityIndex])

  // Transform all availabilities to chips format
  const availabilityChips = useMemo(() => {
    if (!filteredAvailabilities || filteredAvailabilities.length === 0) return []

    return filteredAvailabilities.map((availability, index) => {
      const { all_year, season_start, season_end, recurring_yearly } = availability

      let label = ""
      if (all_year) {
        label = "Available all year"
      } else if (season_start && season_end && selectedYear) {
        // Calculate the intersection of availability range with selected year
        const availStart = moment(season_start)
        const availEnd = moment(season_end)
        const yearStart = moment(`${selectedYear}-01-01`)
        const yearEnd = moment(`${selectedYear}-12-31`)

        // Get the overlapping portion within the selected year
        const displayStart = availStart.isAfter(yearStart) ? availStart : yearStart
        const displayEnd = availEnd.isBefore(yearEnd) ? availEnd : yearEnd

        const startDate = displayStart.format("MMM DD")
        const endDate = displayEnd.format("MMM DD")
        label = `${startDate} - ${endDate}`
      } else if (season_start && season_end) {
        // Fallback for when no year is selected
        const startDate = moment(season_start).format("MMM DD")
        const endDate = moment(season_end).format("MMM DD")
        label = `${startDate} - ${endDate}`
      }

      return {
        index,
        label,
        recurring: recurring_yearly
      }
    })
  }, [filteredAvailabilities, selectedYear])

  // Transform selected availability to component format
  const transformedAvailability = useMemo(() => {
    if (!selectedAvailability) {
      return { schedule: [] }
    }

    const { time_blocks } = selectedAvailability

    // Transform time blocks to schedule format
    const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
    const schedule = daysOfWeek.map((day) => {
      const dayName = day.charAt(0).toUpperCase() + day.slice(1)
      const isAvailable = time_blocks?.[day]

      // If the day is marked as available (true), show "Available"
      // If it's an array, it means there are specific time blocks
      let times = null

      if (Array.isArray(isAvailable) && isAvailable.length > 0) {
        // Handle array of time blocks with start/end times
        times = isAvailable.map((block) => `${block.start} - ${block.end}`)
      } else if (isAvailable === true) {
        // Handle boolean true - available but no specific times
        times = ["Available"]
      }
      // If false or undefined, times stays null (not available)

      return {
        day: dayName,
        times: times && times.length > 0 ? times : null
      }
    })

    return { schedule }
  }, [selectedAvailability])

  const { schedule } = transformedAvailability

  // Transform API data to modal format - use all data, not filtered
  const modalInitialData = useMemo(() => {
    if (!availabilitiesData || availabilitiesData.length === 0) return null

    // Wrap each availability object in an array with proper structure
    return availabilitiesData.map((availability, index) => ({
      id: availability.id || index + 1,
      number: index + 1,
      data: availability
    }))
  }, [availabilitiesData])

  const handleEdit = () => {
    setValidationErrors({})
    setIsEditModalOpen(true)
  }

  const handleBookingConflictCancel = () => {
    setBookingConflictOpen(false)
    setConflictBookings([])
    setConflictCrewName("")
  }

  const handleSave = (formData) => {
    // Clear previous validation errors
    setValidationErrors({})

    // formData contains { availabilities_attributes: [...] }
    return dispatch(updateCrewProfile(crewMemberId, formData))
      .then(() => {
        // Fetch latest profile data to update availability display
        return dispatch(getCrewProfile(crewMemberId))
      })
      .then(() => {
        toaster.success(t("crewMember.availability.messages.updateSuccess", { defaultValue: "Availability updated successfully" }))
        setIsEditModalOpen(false)
      })
      .catch((error) => {
        // Check for booking conflicts - API returns conflicting_bookings array
        if (error?.data?.conflicting_bookings?.length > 0) {
          setConflictBookings(error.data.conflicting_bookings)
          setConflictCrewName(error.data.conflicting_for || "")
          setBookingConflictOpen(true)
          const conflictError = new Error("Booking conflict")
          conflictError.isBookingConflict = true
          throw conflictError
        }

        // Check for validation errors with nested_errors structure
        if (error?.data?.nested_errors?.availabilities_attributes) {
          setValidationErrors(error.data.nested_errors.availabilities_attributes)
          const validationError = new Error("Validation error")
          validationError.isValidationError = true
          throw validationError
        }

        throw error
      })
  }

  // Check scroll position and update arrow visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  // Scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" })
    }
  }

  // Scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" })
    }
  }

  // Check scroll position on mount and when chips change
  useEffect(() => {
    checkScrollPosition()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener("scroll", checkScrollPosition)
      window.addEventListener("resize", checkScrollPosition)
      return () => {
        container.removeEventListener("scroll", checkScrollPosition)
        window.removeEventListener("resize", checkScrollPosition)
      }
    }
  }, [availabilityChips])

  if (profileLoading && availabilitiesData.length === 0) {
    return (
      <Flex vertical gap={16} className={styles.container}>
        <Flex align="center" justify="space-between" className={styles.header}>
          <h2 className={styles.title}>{t("crewMember.availability.title")}</h2>
        </Flex>
        <Card className="py-5xl">
          <Flex justify="center" align="center">
            <Spin size="large" />
          </Flex>
        </Card>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16} className={styles.container}>
      {/* Header */}
      <Flex align="center" justify="space-between" className={styles.header}>
        <h2 className={styles.title}>{t("crewMember.availability.title")}</h2>
        <Flex align="center" gap={12}>
          {/* Year Dropdown - Only show for seasonal availabilities */}
          {hasSeasonalAvailabilities && availableYears.length > 0 && (
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ width: 120 }}
              options={availableYears.map((year) => ({
                label: year.toString(),
                value: year
              }))}
            />
          )}
          {can_edit && (
            <Button type="link" onClick={handleEdit} className={styles.editButton}>
              Edit
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Date Range Filters - Show multiple availability chips */}
      {availabilityChips.length > 0 && (
        <Flex align="center" gap={8} className={styles.filtersWrapper}>
          {showLeftArrow && <Button type="text" icon={<LeftOutlined />} onClick={scrollLeft} className={styles.scrollButton} />}
          <Flex gap={8} wrap align="center" className={styles.filters} ref={scrollContainerRef}>
            {availabilityChips.map((chip) => {
              const isSelected = chip.index === selectedAvailabilityIndex
              return (
                <Tag
                  key={chip.index}
                  className={isSelected ? styles.activeChip : styles.inactiveChip}
                  onClick={() => setSelectedAvailabilityIndex(chip.index)}
                  style={{ cursor: "pointer" }}
                >
                  <span>{chip.label}</span>
                  {chip.recurring && <Rotate useTheme={isSelected} />}
                </Tag>
              )
            })}
          </Flex>
          {showRightArrow && <Button type="text" icon={<RightOutlined />} onClick={scrollRight} className={styles.scrollButton} />}
        </Flex>
      )}
      <WithEmptyStateWrapper
        name="No Availability"
        hasItems={schedule.length > 0}
        fallback={
          <Card className="py-5xl">
            <Flex vertical justify="center" align="center" gap={12}>
              <NoAvailability />
              <Text color={colors.textPrimary} weight="semibold">
                {t("crewMember.availability.emptyState.title")}
              </Text>
              <Text color={colors.textSecondaryDark}>{t("crewMember.availability.emptyState.subtitle")}</Text>
            </Flex>
          </Card>
        }
      >
        {/* Schedule */}
        <Flex vertical gap={4} className={styles.schedule}>
          {schedule.map((item) => {
            const hasTime = item.times && item.times.length > 0
            const isInactive = !hasTime

            return (
              <Flex key={`${item.day}-${item.times?.join("-")}`} align="center" gap={8} className={styles.scheduleRow}>
                <div className={`${styles.dayLabel} ${isInactive ? styles.dayInactive : ""}`}>{item.day}</div>
                <Flex gap={12} wrap="wrap" align="center" className={styles.times}>
                  {hasTime ? (
                    item.times.map((time, timeIndex) => (
                      <span key={time} className={styles.timeText}>
                        {time}
                        {timeIndex < item.times.length - 1 && ","}
                      </span>
                    ))
                  ) : (
                    <span className={styles.noTime}>&nbsp;</span>
                  )}
                </Flex>
              </Flex>
            )
          })}
        </Flex>
      </WithEmptyStateWrapper>
      {/* Edit Availability Modal */}
      <AvailabilityModal
        open={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false)
          setValidationErrors({})
        }}
        onSave={handleSave}
        mode="edit"
        initialData={modalInitialData}
        loading={updating}
        validationErrors={validationErrors}
      />

      {/* Booking Conflict Modal */}
      <BookingConflictModal
        open={bookingConflictOpen}
        onCancel={handleBookingConflictCancel}
        bookings={conflictBookings}
        crewMemberName={conflictCrewName}
        titleKey="crewMember.availability.bookingConflictList.title"
        messageKey="crewMember.availability.bookingConflictList.message"
        closeButtonKey="crewMember.availability.buttons.close"
      />
    </Flex>
  )
}

Availability.propTypes = {
  crewMemberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  can_edit: PropTypes.bool
}

export default Availability
